<template>
  <div class="h-100">
    <vis-toolbar
      v-if="!forbidden && !error"
      :items="toolbar"
      :event-bus="eventBus"
      class="vis-grid-toolbar"
    />

    <div :class="forbidden || error ? 'h-100' : 'vis-has-toolbar'">
      <vis-gistracker-grid
        v-if="ready && !forbidden && !error"
        :event-bus="eventBus"
        @select="onSelect"
        @dblclick="onDblClick"
        @detailview="openDetailView"
      />
      <div v-else-if="ready && forbidden" class="p-4 bg-white h-100">
        <span>{{ $t("errors.Forbidden") }}</span>
      </div>
      <div v-else-if="ready && error" class="p-4 bg-white h-100">
        <span>{{ $t("errors.InternalServerError") }}</span>
      </div>
    </div>

    <vis-confirm-dialog-ex :dialog="deleteDialog"/>
    <vis-confirm-dialog-ex :dialog="restoreDialog" />

    <vis-detail-view-dialog
      v-if="crudService !== null"
      :crud-service="crudService"
      :context="{ Mnemonic: mnemonicName }"
      :visible="dvDialog.visible"
      @save="onDetailViewSave"
      @hide="() => (dvDialog.visible = false)"
    />
  </div>
</template>

<script>
import Vue from "vue";
import api from "@/api/visary-api";
import GisTrackerCrudService from "@/shared/services/gistracker-crud-service";
import VisGistrackerGrid from "@/views/gistracker/vis-gistracker-grid";
import gridCommands from "@/views/base/list-view/commands/grid-commands";

import contextMenuMixin from "@/views/base/list-view/grid/js/context-menu";
import deleteDialogMixin from "@/views/base/list-view/grid/js/dialog/delete";
import restoreDialogMixin from "@/views/base/list-view/grid/js/dialog/restore";
import listViewCommands from "@/views/base/list-view/commands/list-view-commands";
import listViewEvents from "@/views/base/list-view/events/list-view-events";

export default {
  name: "vis-gistracker",
  components: {
    VisGistrackerGrid
  },
  mixins: [
    contextMenuMixin,
    deleteDialogMixin,
    restoreDialogMixin,
  ],
  data(vm) {
    return {
      forbidden: false,
      error: false,
      ready: false,
      mnemonicName: "GisTracker",
      crudService: null,
      config: null,
      showHidden: false,
      eventBus: new Vue(),
      dvDialog: {
        visible: false
      },
      selected: { count: 0, single: null, items: [], keys: [] },
      toolbar: [
        {
          type: "button-group",
          position: "left",
          buttons: [
            {
              id: "add",
              icon: "far fa-plus-circle",
              title: vm.$t("add"),
              disabled: true,
              order: 10,
              onClick(eventBus) {
                eventBus.$emit(listViewCommands.addEntity);
              },
              events(eventBus) {
                let self = this;
                eventBus.$on(listViewEvents.bounded, function () {
                  self.disabled = !vm.config.Permission.Create;
                });
              },
            },
            {
              id: "edit",
              icon: "far fa-pencil",
              title: vm.$t("edit"),
              disabled: true,
              order: 20,
              onClick(eventBus) {
                eventBus.$emit(listViewCommands.editEntity);
              },
              events(eventBus) {
                let self = this;
                eventBus.$on(listViewEvents.bounded, function () {
                  self.disabled = !vm.config.Permission.Update || vm.selected.single == null;
                });
                eventBus.$on(listViewEvents.selected, function () {
                  self.disabled = !vm.config.Permission.Update || vm.selected.single == null;
                });
              },
            },
            {
              id: "delete",
              icon: "far fa-trash",
              title: vm.$t("delete"),
              disabled: true,
              hidden: vm.showHidden,
              order: 30,
              onClick(eventBus) {
                eventBus.$emit(listViewCommands.removeEntity);
              },
              events(eventBus) {
                let self = this;
                eventBus.$on(listViewEvents.bounded, function () {
                  self.disabled = !vm.config.Permission.Delete || vm.selected.count === 0;
                });
                eventBus.$on(listViewEvents.selected, function () {
                  self.disabled = !vm.config.Permission.Delete || vm.selected.count === 0;
                });
              }
            },
            {
              id: "restore",
              icon: "far fa-trash-restore",
              title: vm.$t("restore"),
              disabled: true,
              hidden: false,
              order: 50,
              onClick(eventBus) {
                eventBus.$emit(listViewCommands.restoreEntity);
              },
              events(eventBus) {
                let self = this;
                eventBus.$on(listViewEvents.bounded, function () {
                  self.hidden = !vm.showHidden;
                  self.disabled = vm.selected.count === 0;
                });

                eventBus.$on(listViewEvents.selected, function () {
                  self.hidden = !vm.showHidden;
                  self.disabled = vm.selected.count === 0;
                });
              }
            },
            {
              id: "refresh",
              icon: "far fa-sync",
              title: vm.$t("refresh"),
              order: 60,
              onClick(eventBus) {
                eventBus.$emit(listViewCommands.refresh);
              }
            },
            {
              id: "show-hidden",
              type: "switch",
              iconOff: 'far fa-trash fa-fw',
              iconOn: 'far fa-trash-alt fa-fw',
              title: vm.$t("showHidden"),
              order: 70,
              async onClick(eventBus) {
                eventBus.$emit(listViewCommands.showHidden);
              }
            }
          ]
        }
      ]
    };
  },
  methods: {
    onSelect(event) {
      this.selected = event;
      this.eventBus.$emit(listViewEvents.selected, event);
      this.$emit("select", event);
    },
    onDblClick(event) {
      this.openDetailViewForEditing(event.data.ID)
    },
    openDetailView(data) {
      this.openDetailViewForEditing(data.entityId);
    },
    openDetailViewForEditing(id) {
      if (!id || !this.config.Permission.Update) {
        return;
      }
      this.crudService.setId(id);
      this.dvDialog.visible = true;
    },
    openDetailViewForCreation() {
      if (!this.config.Permission.Create) {
        return;
      }
      this.crudService.setId(0);
      this.dvDialog.visible = true;
    },
    async onDetailViewSave(e) {
      this.eventBus.$emit(gridCommands.refresh);
    },
    onRemove(key) { // used by mixins
      this.refreshGrid();
    },
    dataBind() { // used by mixins
      this.refreshGrid();
    },
    refreshGrid() {
      this.eventBus.$emit(gridCommands.refresh);
    },
  },

  async created() {
    this.crudService = new GisTrackerCrudService(this.mnemonicName);
    this.config = await api.viewConfig.getListView(this.mnemonicName);

    if (this.eventBus) {
      let self = this;

      this.eventBus.$on(listViewCommands.addEntity, function () {
        self.openDetailViewForCreation();
      });

      this.eventBus.$on(listViewCommands.editEntity, function () {
        self.openDetailViewForEditing(self.selected.single.ID);
      });

      this.eventBus.$on(listViewCommands.removeEntity, function () {
        self.deleteDialog.visible = true;
      });

      this.eventBus.$on(listViewCommands.restoreEntity, function () {
        self.restoreDialog.visible = true;
      });

      this.eventBus.$on(listViewCommands.showHidden, function () {
        self.showHidden = !self.showHidden;

        self.toolbar.map((t) =>
          t.buttons.map((b) => {
            if (b.id === "delete") b.hidden = self.showHidden;
            if (b.id === "restore") b.hidden = !self.showHidden;
          })
        );

        self.contextMenu.items.map((m) => {
          if (m.id === "delete") m.hidden = self.showHidden;
          if (m.id === "restore") m.hidden = !self.showHidden;
        });

        self.refreshGrid();
      });

      this.eventBus.$on(listViewCommands.refresh, function () {
        self.refreshGrid();
      });

      self.eventBus.$emit(listViewEvents.bounded);
    }

    this.ready = true;
  },
  destroyed() {
    if(this.eventBus)
      this.eventBus.$destroy();
  },
};
</script>
